import Link from '@/platform/link'
import { usePathname } from '@/platform/navigation'
import type { MouseEvent, ReactNode } from "react"
import {
  LayoutDashboard,
  Receipt,
  Boxes,
  ShoppingBag,
  BarChart3,
  Shield,
  LogOut,
  Store,
  Settings,
  Utensils,
  LayoutGrid,
  ChefHat,
  ListOrdered,
  History,
  Wallet,
} from "lucide-react"
import { useTranslations } from '@/lib/messages'
import { useAuth } from "@/lib/auth-context"
import { BranchSwitcher } from "@/components/branch-switcher"
import { buildApplicationNavItems, type SidebarNavId } from "@/lib/sidebar-nav"
import { canAccessSettingsHub } from "@/lib/settings-nav"
import { useSemanticColors } from "@/lib/semantic-colors"
import { useUnsavedChanges } from "@/lib/unsaved-changes-context"
import { WorkspaceNotificationsNavItem } from "@/components/workspace-notifications-nav-item"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
} from "@/components/ui/sidebar"
import { cn } from "@/lib/utils"

const ICONS: Record<SidebarNavId, typeof LayoutDashboard> = {
  dashboard: LayoutDashboard,
  sales: Receipt,
  stock: Boxes,
  purchasing: ShoppingBag,
  reports: BarChart3,
  admin: Shield,
  restaurant: Utensils,
  "pos-register": Store,
  "pos-touch": LayoutGrid,
  "pos-shifts": Wallet,
  "pos-tables": LayoutGrid,
  "pos-kds": ChefHat,
  "pos-orders": ListOrdered,
  "pos-history": History,
}

const NAV_COLORS: Record<string, { bg: string; fg: string }> = {
  dashboard: { bg: "bg-slate-100 dark:bg-slate-500/15", fg: "text-slate-600 dark:text-slate-300" },
  sales: { bg: "bg-violet-100 dark:bg-violet-500/15", fg: "text-violet-600 dark:text-violet-400" },
  stock: { bg: "bg-emerald-100 dark:bg-emerald-500/15", fg: "text-emerald-600 dark:text-emerald-400" },
  purchasing: { bg: "bg-orange-100 dark:bg-orange-500/15", fg: "text-orange-600 dark:text-orange-400" },
  reports: { bg: "bg-indigo-100 dark:bg-indigo-500/15", fg: "text-indigo-600 dark:text-indigo-400" },
  admin: { bg: "bg-rose-100 dark:bg-rose-500/15", fg: "text-rose-600 dark:text-rose-400" },
  restaurant: { bg: "bg-amber-100 dark:bg-amber-500/15", fg: "text-amber-700 dark:text-amber-400" },
  "pos-register": { bg: "bg-violet-100 dark:bg-violet-500/15", fg: "text-violet-600 dark:text-violet-400" },
  "pos-touch": { bg: "bg-sky-100 dark:bg-sky-500/15", fg: "text-sky-700 dark:text-sky-400" },
  "pos-shifts": { bg: "bg-slate-100 dark:bg-slate-500/15", fg: "text-slate-600 dark:text-slate-300" },
}

function GuardedNavLink({
  href,
  children,
  className,
  ...rest
}: {
  href: string
  children?: ReactNode
  className?: string
} & Record<string, unknown>) {
  const { requestNavigate } = useUnsavedChanges()
  return (
    <Link
      href={href}
      className={className}
      {...rest}
      onClick={(e: MouseEvent<HTMLAnchorElement>) => {
        e.preventDefault()
        requestNavigate(href)
      }}
    >
      {children}
    </Link>
  )
}

export function AppSidebar() {
  const pathname = usePathname()
  const { user, hasModule, logout } = useAuth()
  const { requestLeave } = useUnsavedChanges()
  const tNav = useTranslations("nav")
  const tCommon = useTranslations("common")
  const { enabled: semanticColors } = useSemanticColors()
  const navItems = buildApplicationNavItems(
    (module) => module === "admin" ? user?.isTenantAdmin === true : hasModule(module),
    user?.features,
  )
  const showSettings = canAccessSettingsHub(user?.role)

  return (
    <Sidebar collapsible="offcanvas" side="left">
      <SidebarHeader>
        <BranchSwitcher />
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size="lg" render={<GuardedNavLink href="/dashboard" />}>
              <div className="flex aspect-square size-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                <LayoutDashboard className="size-4" />
              </div>
              <div className="grid flex-1 text-start text-sm leading-tight">
                <span className="truncate font-semibold">{tCommon("appName")}</span>
              </div>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {user?.features?.multiBranch ? (
          <SidebarGroup>
            <SidebarGroupLabel>{tNav("notificationsSection")}</SidebarGroupLabel>
            <SidebarMenu>
              <WorkspaceNotificationsNavItem />
            </SidebarMenu>
          </SidebarGroup>
        ) : null}
        <SidebarGroup>
          <SidebarGroupLabel>{tNav("application")}</SidebarGroupLabel>
          <SidebarMenu>
            {user?.isTenantAdmin && user.features?.multiBranch && <>
              <SidebarMenuItem>
                <SidebarMenuButton render={<GuardedNavLink href="/dashboard/branches/overview" />} isActive={pathname === "/dashboard/branches/overview"}>
                  <LayoutDashboard /><span>{tNav("branchDashboard")}</span>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </>}
            {navItems.map((item) => {
              const Icon = ICONS[item.id]
              const colors = NAV_COLORS[item.colorKey]
              const label = tNav(item.labelKey as "dashboard")
              return (
                <SidebarMenuItem key={item.id}>
                  <SidebarMenuButton
                    render={<GuardedNavLink href={item.href} />}
                    isActive={item.isActive(pathname)}
                    tooltip={label}
                  >
                    {semanticColors ? (
                      <span
                        className={cn(
                          "nav-module-icon flex size-6 shrink-0 items-center justify-center rounded-md",
                          colors.bg,
                          colors.fg,
                        )}
                        data-nav-module={item.id}
                      >
                        <Icon className="size-3.5" />
                      </span>
                    ) : (
                      <Icon />
                    )}
                    <span>{label}</span>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )
            })}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <div className="flex flex-col gap-1 px-2 py-3">
              <span className="truncate text-sm font-medium">{user?.username}</span>
              {user?.role && (
                <span className="truncate text-xs text-muted-foreground">{user.role}</span>
              )}
            </div>
          </SidebarMenuItem>
          {showSettings ? (
            <SidebarMenuItem>
              <SidebarMenuButton
                render={<GuardedNavLink href="/dashboard/settings" />}
                isActive={pathname.startsWith("/dashboard/settings")}
                tooltip={tNav("settings")}
              >
                <Settings />
                <span>{tNav("settings")}</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ) : null}
          <SidebarMenuItem>
            <SidebarMenuButton onClick={() => requestLeave(logout)} tooltip={tNav("signOut")}>
              <LogOut />
              <span>{tNav("signOut")}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  )
}
